import * as React from "react";
import Box from "@mui/material/Box";

import {
  ClickAwayListener,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Tooltip,
} from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import styled from "@emotion/styled";
import {
  H6HeadlineSmall,
  BodyLarge,
} from "../../components/StyledComponents/Typography/Typography.tsx";
import { SecondaryButton } from "../../components/StyledComponents/Buttons/AuradineButtons.js";
import { useCaseCommands } from "../queries/useCaseCommands.ts";
import { useExportCases } from "../queries/useExportCases.ts";
import useAppContextProvider from "../../AppContext/useAppContextProvider.js";
import CreateCaseModal from "./CreateCaseModal.js";
import AddCommentModal from "./AddCommentModal.js";
import EscalateCaseModal from "./EscalateCaseModal.js";
import ReopenCaseModal from "./ReopenCaseModal.js";
import CloseCaseModal from "./CloseCaseModal.js";
import { useTheme } from "../../ThemeContext/ThemeContext.js";

const StyledPopoverContent = styled(Box)(({ colors }) => ({
  display: "flex",
  flexDirection: "column",
  background: colors?.background?.paper,
  borderRadius: "1rem",
  backgroundColor: colors?.background?.paper,
  boxShadow: "0px 2px 6px 2px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)",
}));

const StyledPopover = styled(Popover,)(({ colors }) => ({
  "& .MuiPaper-root": {
    borderRadius: "1rem", 
    backgroundColor: colors?.background?.popover,
  },
}));

const StyledHeaderAndFooterBox = styled(Box)`
  padding: 1.5rem;
`;

const MoreOptionsCard = (props) => {
  const {
    open,
    handleClose,
    anchorRef,
    setTableKey,
    refetch,
    role,
    selectedCases,
    setCreateCaseModalOpen,
    setAddCommentModalOpen,
    setEscalateCaseModalOpen,
    setReopenCaseModalOpen,
    setCloseCaseModalOpen,
    userData,
  } = props;
  const { colors } = useTheme();
  const {
    mutate: sendCaseCommands,
  } = useCaseCommands();

  const {
    mutate: exportCases,
  } = useExportCases();

  const { setToastMessage, setToastOpen } = useAppContextProvider();

  const hasSelectedCases =
    Array.isArray(selectedCases) &&
    selectedCases.length > 0;

  const hasMultipleSelectedCases =
    Array.isArray(selectedCases) &&
    selectedCases.length > 1;

  // For Case Management, enable actions when rows are selected
  const isDisabled = !hasSelectedCases;

  // For bulk actions, require multiple cases
  const isBulkActionDisabled = !hasMultipleSelectedCases;

  const menuOptions = [
    {
      title: "Add Comment",
      endIcon: () => (
        <span className="material-symbols-outlined" style={{ color: colors?.text?.muted }}>
          comment
        </span>
      ),
      onClick: () => {
        setAddCommentModalOpen(true);
        handleClose();
      },
      disabled: isDisabled,
      needDivider: false,
    },
    {
      title: "Escalate Case",
      endIcon: () => (
        <span className="material-symbols-outlined" style={{ color: colors?.text?.muted }}>
          priority_high
        </span>
      ),
      onClick: () => {
        setEscalateCaseModalOpen(true);
        handleClose();
      },
      disabled: isDisabled,
      needDivider: false,
    },
    {
      title: "Reopen Case",
      endIcon: () => (
        <span className="material-symbols-outlined" style={{ color: colors?.text?.muted }}>
          refresh
        </span>
      ),
      onClick: () => {
        setReopenCaseModalOpen(true);
        handleClose();
      },
      disabled: isDisabled,
      needDivider: false,
    },
    {
      title: "Close Case",
      endIcon: () => (
        <span className="material-symbols-outlined" style={{ color: colors?.text?.muted }}>
          lock
        </span>
      ),
      onClick: () => {
        setCloseCaseModalOpen(true);
        handleClose();
      },
      disabled: isDisabled,
      needDivider: true,
    },

    {
      title: "Download CSV",
      endIcon: () => (
        <span className="material-symbols-outlined" style={{ color: colors?.text?.muted }}>
          csv
        </span>
      ),
      onClick: () => {
        // Extract org_id from user object, with fallback to default
        const orgId = userData?.org_id;
        
        exportCases(
          {
            AccountId: orgId,
            pagination: {
              limit: "100",
              skip: "0"
            },
            filters: [
              {
                caseId: selectedCases || []
              }
            ]
          },
          {
            onSuccess: async (response) => {
              if (response.status === "ok") {
                setToastMessage(
                  "CSV file has been downloaded successfully"
                );
                setToastOpen(true);
                console.log(
                  "CSV file downloaded successfully"
                );
              } else {
                setToastMessage(
                  `Error downloading CSV file: ${response.msg}`
                );
                setToastOpen(true);
              }
            },
            onError: (error) => {
              setToastMessage(
                `Error downloading CSV file: ${error}`
              );
              setToastOpen(true);
            },
          }
        );
        handleClose();
      },
      disabled: false, // Always enable Download CSV
      needDivider: false,
    },
  ];

  return (
    <>
      <StyledPopover
        sx={{
          zIndex: 9999,
          borderRadius: "1rem",
        }}
        open={open}
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        colors={colors}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <StyledPopoverContent
            colors={colors}
            display={"flex"}
            flexGrow={1}
          >
            <StyledHeaderAndFooterBox
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"flex-start"}
            >
              <H6HeadlineSmall>
                Case Management Actions
              </H6HeadlineSmall>
            </StyledHeaderAndFooterBox>

            <Box
              display={"flex"}
              flexDirection={"column"}
              pb={"1rem"}
            >
              <Box
                display={"flex"}
                maxHeight={"25rem"}
                overflow={"auto"}
                flexDirection={"column"}
                sx={{
                  scrollbarWidth: "thin",
                  scrollBehavior: "smooth ",
                  willChange: "transform",
                }}
              >
                {menuOptions.map((option, index) => (
                  <React.Fragment key={index}>
                    <ListItemButton
                      onClick={option.onClick}
                      sx={{
                        borderRadius: "1rem",
                        padding: "0.5rem 1rem",
                        margin: "0.5rem 0",
                        backgroundColor: colors?.background?.paper,
                        "&:hover": {
                          backgroundColor: colors?.hover?.muted,  
                        },
                      }}
                      disabled={
                        option.disabled ? true : false
                      }
                    >
                      <ListItemText
                        primary={
                          <BodyLarge>
                            {option.title}
                          </BodyLarge>
                        }
                      />
                      <ListItemIcon
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        {option.endIcon()}
                      </ListItemIcon>
                    </ListItemButton>
                    {option.needDivider && <Divider />}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          </StyledPopoverContent>
        </ClickAwayListener>
      </StyledPopover>
    </>
  );
};

export const CaseManagementActionsTab = (props) => {
  const {
    downloadCSV,
    setTableKey,
    refetch,
    role,
    selectedCases,
    caseData,
    user: userData,
  } = props;
  const { colors } = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [createCaseModalOpen, setCreateCaseModalOpen] = React.useState(false);
  const [addCommentModalOpen, setAddCommentModalOpen] = React.useState(false);
  const [escalateCaseModalOpen, setEscalateCaseModalOpen] = React.useState(false);
  const [reopenCaseModalOpen, setReopenCaseModalOpen] = React.useState(false);
  const [closeCaseModalOpen, setCloseCaseModalOpen] = React.useState(false);

  const anchorRef = React.useRef(null);
  const { user, getAccessTokenSilently } = useAuth0();

  const hasSelectedCases =
    Array.isArray(selectedCases) &&
    selectedCases.length > 0;

  const hasMultipleSelectedCases =
    Array.isArray(selectedCases) &&
    selectedCases.length > 1;

  // For Case Management, enable actions when rows are selected
  const isDisabled = !hasSelectedCases;

  // For bulk actions, require multiple cases
  const isBulkActionDisabled = !hasMultipleSelectedCases;

  const handleClick = (event) => {
    event.stopPropagation();
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          textAlign: "center",
          gap: 2,
        }}
      >
        <Tooltip
          title="Create a new case"
          arrow
        >
          <span>
            <SecondaryButton colors={colors}
              endIcon={
                <span className="material-symbols-outlined">
                  add_task
                </span>
              }
              onClick={() => setCreateCaseModalOpen(true)}
            >
              Create Case
            </SecondaryButton>
          </span>
        </Tooltip>
        <Tooltip
          title="Perform actions on selected cases"
          arrow
        >
          <span>
            <SecondaryButton colors={colors}
              ref={anchorRef}
              endIcon={
                <span className="material-symbols-outlined">
                  assignment_add
                </span>
              }
              onClick={(event) => handleClick(event)}
            >
              Actions
            </SecondaryButton>
          </span>
        </Tooltip>
      </Box>

      <MoreOptionsCard
        open={open}
        handleClose={handleClose}
        anchorRef={anchorRef}
        downloadCSV={downloadCSV}
        setTableKey={setTableKey}
        refetch={refetch}
        role={role}
        selectedCases={selectedCases}
        setCreateCaseModalOpen={setCreateCaseModalOpen}
        setAddCommentModalOpen={setAddCommentModalOpen}
        setEscalateCaseModalOpen={setEscalateCaseModalOpen}
        setReopenCaseModalOpen={setReopenCaseModalOpen}
        setCloseCaseModalOpen={setCloseCaseModalOpen}
        userData={userData}
      />

      <CreateCaseModal
        open={createCaseModalOpen}
        setOpen={setCreateCaseModalOpen}
        refetch={refetch}
        setTableKey={setTableKey}
      />

      <AddCommentModal
        open={addCommentModalOpen}
        setOpen={setAddCommentModalOpen}
        refetch={refetch}
        setTableKey={setTableKey}
        selectedCases={selectedCases}
        caseData={caseData}
      />

      <EscalateCaseModal
        open={escalateCaseModalOpen}
        setOpen={setEscalateCaseModalOpen}
        refetch={refetch}
        setTableKey={setTableKey}
        selectedCases={selectedCases}
        caseData={caseData}
      />

      <ReopenCaseModal
        open={reopenCaseModalOpen}
        setOpen={setReopenCaseModalOpen}
        refetch={refetch}
        setTableKey={setTableKey}
        selectedCases={selectedCases}
        caseData={caseData}
      />

      <CloseCaseModal
        open={closeCaseModalOpen}
        setOpen={setCloseCaseModalOpen}
        refetch={refetch}
        setTableKey={setTableKey}
        selectedCases={selectedCases}
        caseData={caseData}
      />


    </React.Fragment>
  );
};
